/* =====================================================================================
   Registry: the company's own legal record.

   Three kinds of document that had nowhere to live. CONSTITUTIONAL, the certificate of
   incorporation and articles, which open every diligence checklist. STATUTORY, the registers and
   board minutes a company is legally required to keep, where the requirement is not satisfied by
   them existing somewhere on a laptop. And EXECUTED AGREEMENTS, signed NDAs and appointment
   letters.

   NOT Contracts, which is commercial agreements with customers. NOT Templates, which holds blanks.
   NOT Canon, which is the rules the company follows. Registry is what the company IS.

   IT SHOWS WHAT IS MISSING. A register that lists only what you have is a register that cannot
   tell you the articles were never filed anywhere. The statutory skeleton is seeded with no file
   attached, so the gap is the first thing on screen.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import './registry.css';

interface Doc {
  id: string; category: string; title: string; counterparty: string | null;
  reference: string | null; document_date: string | null; status: string;
  summary: string | null; original_location: string | null;
  file_path: string | null; file_size: number | null;
}

const CATS: [string, string, string][] = [
  ['constitutional', 'Constitutional', 'Certificate of incorporation and articles. The first page of every diligence checklist.'],
  ['board', 'Board and directors', 'Minutes, written resolutions and the statutory registers. Minutes must be kept for ten years.'],
  ['shares', 'Shares', 'Allotments, transfers, certificates. The register of members, not the Companies House filing, legally determines who owns the company.'],
  ['psc', 'Persons with significant control', 'Statutory register.'],
  ['confidentiality', 'Confidentiality agreements', 'Executed NDAs. Blank templates live in Templates.'],
  ['appointment', 'Appointments', 'Director, non-executive and adviser letters.'],
  ['insurance', 'Insurance', ''],
  ['financing', 'Financing', ''],
  ['filing', 'Filings', ''],
  ['property', 'Property', ''],
  ['other', 'Other', ''],
];

const size = (b: number | null) =>
  b == null ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

export function RegistryView() {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [nu, setNu] = useState({ category: 'confidentiality', title: '', counterparty: '',
                                 document_date: '', status: 'executed' });

  const load = useCallback(async () => {
    if (BACKEND !== 'supabase') { setRows([]); return; }
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().schema('app').from('registry_documents')
        .select('id, category, title, counterparty, reference, document_date, status, summary,'
              + ' original_location, file_path, file_size')
        .order('category').order('document_date', { ascending: false, nullsFirst: false });
      if (error) throw new Error(error.message);
      setRows((data ?? []) as unknown as Doc[]);
    } catch (e) { setErr(String((e as Error).message)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function download(d: Doc) {
    if (!d.file_path) return;
    setBusy(d.id);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().storage.from('registry')
        .createSignedUrl(d.file_path, 120, { download: true });
      if (error) throw new Error(error.message);
      window.location.href = data.signedUrl;
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function upload(d: Doc, file: File) {
    /* Checked here as well as at the bucket. The bucket refusal is a raw storage error; this is a
       sentence somebody can act on. */
    const ok = ['application/pdf',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/msword'];
    if (!ok.includes(file.type)) {
      setMsg('PDF or Word only. A legal record should not be a screenshot.'); return;
    }
    setBusy(d.id); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: org } = await supabase().schema('app').rpc('current_org_id');
      const path = `${org}/${d.category}/${d.id}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
      const { error: upErr } = await supabase().storage.from('registry')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw new Error(upErr.message);
      /* A placeholder that now has a file is no longer 'required'. A real draft stays a draft:
         somebody chose that, and an upload is not a reason to overrule them. */
      const patch: Record<string, unknown> = {
        file_path: path, mime: file.type, file_size: file.size,
      };
      if (d.status === 'required') patch.status = 'held';
      const { error: rowErr } = await supabase().schema('app').from('registry_documents')
        .update(patch).eq('id', d.id);
      if (rowErr) throw new Error(`Uploaded, but not recorded: ${rowErr.message}`);
      setMsg(d.status === 'required'
        ? `${d.title} attached and marked as held.`
        : `${d.title} attached.`);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  /* Editable in place. The register's whole job is to be current, and a status nobody can change
     goes stale on the first upload. */
  const STATUSES = ['required', 'held', 'executed', 'awaiting_signature', 'draft',
                    'superseded', 'expired'];

  async function setStatus(d: Doc, status: string) {
    setBusy(d.id);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('app').from('registry_documents')
        .update({ status }).eq('id', d.id);
      if (error) throw new Error(error.message);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function add() {
    if (!nu.title.trim()) { setMsg('It needs a title.'); return; }
    setBusy('new');
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('app').from('registry_documents').insert({
        category: nu.category, title: nu.title.trim(),
        counterparty: nu.counterparty.trim() || null,
        document_date: nu.document_date || null, status: nu.status,
      });
      if (error) throw new Error(error.message);
      setAdding(false);
      setNu({ category: 'confidentiality', title: '', counterparty: '', document_date: '', status: 'executed' });
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  if (err) return <p className="block-error" role="alert">{err}</p>;
  if (!rows) return <p className="muted">Loading</p>;

  /* What we are obliged to hold and do not, which is the question the register exists to answer.
     Counting everything without a file included executed agreements filed elsewhere and made the
     number meaningless. */
  const missing = rows.filter((d) => d.status === 'required').length;
  const unattached = rows.filter((d) => !d.file_path).length;

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Registry</h1>
        <p className="page-sub">
          {TENANT.brand.orgName}&rsquo;s own legal record: constitutional, statutory, and executed
          agreements. What would be handed to a solicitor, an investor or Companies House.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      <section className="card reg-summary">
        <p>
          {missing > 0 ? (
            <>
              <strong>{missing}</strong> document{missing === 1 ? '' : 's'} the company is
              required to hold {missing === 1 ? 'is' : 'are'} still outstanding.
              The obligation is not satisfied by the document existing on somebody&rsquo;s laptop.
            </>
          ) : (
            <>Everything the company is required to hold is recorded.</>
          )}
          {unattached > 0 && (
            <> {unattached} entr{unattached === 1 ? 'y has' : 'ies have'} no file attached.</>
          )}
        </p>
        <button className="btn primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : 'Add an entry'}
        </button>
      </section>

      {adding && (
        <section className="card reg-add">
          <select className="input sel" value={nu.category}
                  onChange={(e) => setNu({ ...nu, category: e.target.value })}>
            {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input className="input grow" placeholder="Title" value={nu.title}
                 onChange={(e) => setNu({ ...nu, title: e.target.value })} />
          <input className="input" placeholder="Counterparty, if any" value={nu.counterparty}
                 onChange={(e) => setNu({ ...nu, counterparty: e.target.value })} />
          <input className="input" type="date" value={nu.document_date}
                 onChange={(e) => setNu({ ...nu, document_date: e.target.value })} />
          <select className="input sel" value={nu.status}
                  onChange={(e) => setNu({ ...nu, status: e.target.value })}>
            {['executed', 'held', 'awaiting_signature', 'draft', 'superseded', 'expired']
              .map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <button className="btn primary" disabled={busy === 'new'} onClick={add}>Add</button>
        </section>
      )}

      {CATS.filter(([k]) => rows.some((d) => d.category === k)).map(([k, label, note]) => (
        <section key={k} className="card">
          <h2 className="reg-cat">{label}</h2>
          {note && <p className="reg-note">{note}</p>}
          {/* The table is wider than the card on a narrow window, and without this the file column
              rendered outside the card's border. */}
          <div className="table-wrap">
          <table className="data">
            <thead>
              <tr><th>Document</th><th>Date</th><th>Status</th><th>File</th></tr>
            </thead>
            <tbody>
              {rows.filter((d) => d.category === k).map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="cell-title">{d.title}</div>
                    {d.counterparty && <div className="cell-sub">{d.counterparty}</div>}
                    {d.summary && <div className="cell-sub">{d.summary}</div>}
                    {!d.file_path && d.original_location && (
                      /* Where the file is on disk, so attaching it is one click and a known path
                         rather than a hunt through four folders. */
                      <div className="cell-sub reg-path">on disk: {d.original_location}</div>
                    )}
                  </td>
                  <td>{d.document_date ?? ''}</td>
                  <td>
                    <select className={`input sel reg-status-sel st-${d.status}`}
                            value={d.status} disabled={busy === d.id}
                            aria-label={`Status of ${d.title}`}
                            onChange={(e) => setStatus(d, e.target.value)}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </td>
                  <td className="reg-file">
                    {d.file_path ? (
                      <>
                        <button className="link-btn" disabled={busy === d.id}
                                onClick={() => download(d)}>Download</button>
                        <span className="cell-sub">{size(d.file_size)}</span>
                      </>
                    ) : <span className="reg-missing">not attached</span>}
                    <label className="btn reg-upload">
                      {d.file_path ? 'Replace' : 'Upload'}
                      <input type="file" hidden accept=".pdf,.docx,.doc"
                             onChange={(e) => {
                               const f = e.target.files?.[0];
                               if (f) upload(d, f);
                               e.target.value = '';
                             }} />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      ))}
    </div>
  );
}
